import { makeAutoObservable, runInAction } from 'mobx';
import { apiService } from '../services/api';
import { toastStore } from './toastStore';

export interface User {
  id: string;
  tenant_id: string;
  email: string;
  name: string;
  role: 'root' | 'admin' | 'user';
  created_at?: string;
  updated_at?: string;
  last_login_at?: string;
  createdAt?: string;
  lastLoginAt?: string;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  role: 'admin' | 'user';
  password: string;
}

export interface UpdateUserRequest {
  name: string;
  role: 'admin' | 'user';
  password?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

class UserStore {
  users: User[] = [];
  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchUsers() {
    this.loading = true;
    try {
      const response = await apiService.get<ApiResponse<User[]>>('/v1/admin/users');
      runInAction(() => {
        this.users = response.data || [];
        this.loading = false;
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'ユーザーの取得に失敗しました';
      toastStore.showError(errorMessage);
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async createUser(data: CreateUserRequest) {
    this.loading = true;
    try {
      const response = await apiService.post<ApiResponse<User>>('/v1/admin/users', data);
      runInAction(() => {
        this.users.push(response.data);
        this.loading = false;
      });
      toastStore.showSuccess('ユーザーを作成しました');
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'ユーザーの作成に失敗しました';
      toastStore.showError(errorMessage);
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  async updateUser(userId: string, data: UpdateUserRequest) {
    this.loading = true;
    try {
      const response = await apiService.put<ApiResponse<User>>(`/v1/admin/users/${userId}`, data);
      runInAction(() => {
        const index = this.users.findIndex((u) => u.id === userId);
        if (index !== -1) {
          this.users[index] = response.data;
        }
        this.loading = false;
      });
      toastStore.showSuccess('ユーザー情報を更新しました');
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'ユーザーの更新に失敗しました';
      toastStore.showError(errorMessage);
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  async deleteUser(userId: string) {
    this.loading = true;
    try {
      await apiService.delete(`/v1/admin/users/${userId}`);
      runInAction(() => {
        this.users = this.users.filter((u) => u.id !== userId);
        this.loading = false;
      });
      toastStore.showSuccess('ユーザーを削除しました');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'ユーザーの削除に失敗しました';
      toastStore.showError(errorMessage);
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

}

export const userStore = new UserStore();